/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata/src';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import { getAngularRef } from '../getAngularRef/getAngularRef';
import {
	EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER,
	EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER,
	EDITOR_WORD_COUNT
} from '../../constants/identifiers';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';

export function getOnReady( editorType: EditorType, toolbarOptions: ToolbarOptions, editorPlugins: EditorPlugins ): string {
	const hasWordCount = requiresWordCountPlugin( editorPlugins );
	const isDecoupled = isDecoupledEditor( editorType );

	if ( !isDecoupled && !hasWordCount ) {
		return '';
	}

	const menuBarUsed = toolbarOptions.isMenuBarUsed;
	const editorToolbar = getAngularRef( EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER ).nativeElement;
	const editorMenuBar = getAngularRef( EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER ).nativeElement;
	const wordCount = getAngularRef( EDITOR_WORD_COUNT ).nativeElement;

	return `
		public onReady(editor: ${ editorType }): void {
		${ [
		hasWordCount ? `Array.from(${ wordCount }.children).forEach(child => child.remove());` : '',
		isDecoupled ? `Array.from(${ editorToolbar }.children).forEach(child => child.remove());` : '',
		menuBarUsed ? `Array.from(${ editorMenuBar }.children).forEach(child => child.remove());` : '',
		'\n',
		hasWordCount ? 'const wordCount = editor.plugins.get(\'WordCount\');' : '',
		hasWordCount ? `${ wordCount }.appendChild(wordCount.wordCountContainer);` : '',
		isDecoupled ? `${ editorToolbar }.appendChild(editor.ui.view.toolbar.element!);` : '',
		menuBarUsed ? `${ editorMenuBar }.appendChild(editor.ui.view.menuBarView.element!);` : ''
	].filter( Boolean ).join( '\n' ) }
		}
	`;
}
